import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, useToast, Dialog } from '../components/ui';
import DemographicsPanel from '../components/DemographicsPanel';
import PropertyLoanPanel from '../components/PropertyLoanPanel';
import CallPrepButton from '../components/CallPrepButton';
import Layout from '../components/Layout';
import ZoningPanel from '../components/ZoningPanel';
import WhatCouldBeBuilt from '../components/WhatCouldBeBuilt';
import PropertyDocumentsPanel from '../components/PropertyDocumentsPanel';
import Breadcrumbs from '../components/Breadcrumbs';
import DuplicatePropertyDialog from '../components/DuplicatePropertyDialog';
import LoiGeneratorDialog from '../components/LoiGeneratorDialog';
import { recordFactsOf } from '../components/loi/recordFacts';
import { recordSiteSize } from '../components/loi/recordSiteSize';
import LeaseProposalGeneratorDialog from '../components/LeaseProposalGeneratorDialog';
import ProposalGeneratorDialog from '../components/ProposalGeneratorDialog';
import BrochureGeneratorDialog, { type BrochurePrefill } from '../components/BrochureGeneratorDialog';
import {
  duplicateCandidatesFrom,
  friendlyErrorMessage,
  propertyIdFrom,
  DuplicateCandidate,
  DuplicateAction,
  createSubmitLock,
} from '../utils/duplicateChoice';
import { api } from '../lib/api';
import { STREET_TILES } from '../lib/mapTiles';
import type { OwnerResearch, OwnerEntityType, ResearchSource, ManagementTier } from '../types';
import { useSetPageSubject, type PageSubject } from '../contexts/PageSubjectContext';
import { usePageAction, type PageActionFields } from '../contexts/PageActionContext';
import { isLiveListing, latestClosedSale } from '../utils/latestSale';
import { displayDate } from '../utils/dates';

interface PooledComp {
  id: string;
  comp_property_id?: string;
  address?: string;
  city?: string;
  state?: string;
  sale_price?: number;
  price_per_sf?: number;
  cap_rate?: number;
  sale_date?: string;
  distance_miles?: number;
  similarity_score?: number;
  source?: string;
  building_size?: number;
}

interface MarketIntelItem {
  id: string;
  intel_type: string;
  impact: string;
  title: string;
  description: string;
  effective_date?: string;
  source?: string;
  confidence_level?: string;
}

const FRESHNESS_BADGE: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'default' }> = {
  current: { label: 'Current', variant: 'success' },
  needs_review: { label: 'Needs review', variant: 'warning' },
  stale: { label: 'Stale', variant: 'error' },
  unverified: { label: 'Unverified', variant: 'warning' },
};

// Lease-expiration urgency — same thresholds as the map's tenant pins
function tenantExpiryColor(iso?: string | null): string {
  if (!iso) return '#9ca3af';
  const months = (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44);
  if (months <= 12) return '#dc2626';
  if (months <= 24) return '#d97706';
  return '#0d9488';
}

const IMPACT_VARIANT: Record<string, 'success' | 'error' | 'default' | 'warning'> = {
  positive: 'success',
  negative: 'error',
  neutral: 'default',
  uncertain: 'warning',
};

/** The record card's tabs (OO2). Real tabs: each shows what the page used to stack in one scroll. */
type RecordTab = 'overview' | 'financials' | 'comps' | 'tenants' | 'intel';
const RECORD_TABS: { key: RecordTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'financials', label: 'Financials' },
  { key: 'comps', label: 'Comps' },
  { key: 'tenants', label: 'Tenants' },
  { key: 'intel', label: 'Intel' },
];

/** The pipeline stages the dark "Deal status" block sets; `includes` maps stored statuses onto them. */
const PIPELINE_STEPS = [
  { key: 'prospect', label: 'Prospect', short: 'Prospect', includes: ['prospect', 'contacted', 'pitched'] },
  { key: 'listed', label: 'Actively Marketing', short: 'Marketing', includes: ['listed'] },
  { key: 'under_contract', label: 'Under Contract', short: 'Contract', includes: ['under_contract'] },
  { key: 'sold', label: 'Closed', short: 'Closed', includes: ['sold', 'leased'] },
];

interface PropertyData {
  id: string;
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
  property_name?: string;
  building_park?: string;
  apn?: string;
  costar_id?: string;
  crexi_id?: string;
  unit_suite?: string;
  property_type?: string;
  property_subtype?: string;
  building_class?: string;
  building_status?: string;
  zoning?: string;
  /** FZ2: the zone READ FROM THE MAP, its source, and whose code governs. Null until resolved. */
  zoning_normalized?: string | null;
  zoning_source?: string | null;
  zoning_jurisdiction?: string | null;
  submarket?: string;
  market?: string;
  building_size?: number;
  land_area_sf?: number;
  lot_size_acres?: number;
  typical_floor_size?: number;
  number_of_floors?: number;
  number_of_units?: number;
  number_of_buildings?: number;
  number_of_addresses?: number;
  year_built?: number;
  month_built?: number;
  year_renovated?: number;
  month_renovated?: number;
  construction_material?: string;
  clear_height_ft?: number;
  dock_doors?: number;
  grade_doors?: number;
  rail_served?: boolean;
  column_spacing?: string;
  sprinkler_type?: string;
  number_of_cranes?: number;
  power?: string;
  office_percentage?: number;
  office_space?: number;
  number_of_elevators?: number;
  parking_spaces?: number;
  parking_ratio?: number;
  percent_leased?: number;
  vacancy_percent?: number;
  days_on_market?: number;
  rent_per_sf?: number;
  avg_weighted_rent?: number;
  cross_street?: string;
  opportunity_zone?: boolean;
  latitude?: number;
  longitude?: number;
  owner_name?: string;
  owner_contact?: string;
  owner_address?: string;
  owner_phone?: string;
  mailing_city?: string;
  mailing_state?: string;
  mailing_zip?: string;
  mailing_care_of?: string;
  parent_company?: string;
  fund_name?: string;
  property_manager_name?: string;
  property_manager_phone?: string;
  leasing_company_name?: string;
  leasing_company_contact?: string;
  leasing_company_phone?: string;
  developer_name?: string;
  architect_name?: string;
  improvement_value?: number;
  land_value?: number;
  total_parcel_value?: number;
  parcel_value_type?: string;
  tax_year?: number;
  annual_tax_bill?: number;
  water?: string;
  sewer?: string;
  gas?: string;
  amenities?: string;
  features?: string;
  source?: string;
  pipeline_status?: string;
  freshness_status?: string;
  verified_at?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface Transaction {
  id: string;
  transaction_type: string;
  transaction_date?: string;
  recording_date?: string;
  // Sale
  sale_price?: number;
  price_per_sf?: number;
  cap_rate?: number;
  noi?: number;
  // Parties
  buyer_name?: string;
  seller_name?: string;
  broker_name?: string;
  broker_company?: string;
  // Lease
  tenant_name?: string;
  lease_type?: string;
  lease_term_months?: number;
  rent_per_sf_year?: number;
  lease_start_date?: string;
  lease_end_date?: string;
  // Listing
  asking_price?: number;
  asking_price_per_sf?: number;
  days_on_market?: number;
  listing_status?: string;
  asking_cap_rate?: number;
  /** CoStar "Sale Status" (Active / Under Contract / Escrow / Sold). */
  for_sale_status?: string;
  // Financing
  lender?: string;
  loan_amount?: number;
  loan_type?: string;
  interest_rate?: number;
  maturity_date?: string;
  // Meta
  source?: string;
  notes?: string;
  created_at?: string;
}

const formatCurrency = (value: number | null | undefined) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const formatNumber = (value: number | null | undefined) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  return displayDate(value, { year: 'numeric', month: 'short', day: 'numeric' }) ?? '-';
};

// A labeled field, shown only when there is a value: the design's spec-grid cell.
function Field({ label, value }: { label: string; value: string | number | boolean | null | undefined }) {
  if (value == null || value === '' || value === false) return null;
  const display = typeof value === 'boolean' ? 'Yes' : String(value);
  return (
    <div className="min-w-0">
      <dt className="mb-[5px] text-[11px] leading-tight text-gray-500">{label}</dt>
      <dd className="break-words text-[13.5px] font-medium leading-snug tabular-nums text-gray-800">{display}</dd>
    </div>
  );
}

// A section of the property record: the design's red eyebrow over a four-column spec grid.
function RecordSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-[18px] border-t border-gray-150 pt-[18px]">
      <div className="mb-3.5 text-[9.5px] font-bold uppercase leading-none tracking-[.14em] text-primary-500">{title}</div>
      <dl className="grid grid-cols-2 gap-x-[22px] gap-y-[18px] sm:grid-cols-3 lg:grid-cols-4">{children}</dl>
    </div>
  );
}

// Returns true if the property has at least one non-empty value for any of the given keys
function hasAnyValue(obj: PropertyData, keys: (keyof PropertyData)[]) {
  return keys.some((k) => {
    const v = obj[k];
    return v != null && v !== '' && v !== false;
  });
}

// ── Transaction Expandable Card ─────────────────────────────────
function TransactionCard({ tx }: { tx: Transaction }) {
  const [expanded, setExpanded] = useState(false);

  const typeBadge: Record<string, { color: string; label: string }> = {
    sale: { color: 'bg-success-wash text-success', label: 'Sale' },
    lease: { color: 'bg-info-wash text-info', label: 'Lease' },
    listing: { color: 'bg-gray-100 text-gray-800', label: 'Listing' },
    off_market: { color: 'bg-gray-100 text-gray-800', label: 'Off Market' },
  };

  // A "sale" row with no closing date but a live CoStar status is an OPEN
  // LISTING, not a closed comp - and for these rows CoStar puts the ASKING
  // price in sale_price. Rendering them as a green "Sale" with a blank date
  // reads as a sale with missing data, and risks an ask being quoted as a trade.
  const live = isLiveListing(tx);
  const liveLabel = tx.for_sale_status
    ? tx.for_sale_status.replace(/\b\w/g, (c) => c.toUpperCase())
    : 'For Sale';
  /** For live listings the ask lives in sale_price (asking_price is rarely populated). */
  const liveAsk = tx.asking_price ?? tx.sale_price;

  const badge = live
    ? { color: 'bg-warning-wash text-warning', label: liveLabel }
    : typeBadge[tx.transaction_type] || typeBadge.off_market;

  // Build headline depending on type
  const headline = live
    ? `Asking ${formatCurrency(liveAsk)}`
    : tx.transaction_type === 'sale'
    ? formatCurrency(tx.sale_price)
    : tx.transaction_type === 'lease'
    ? (tx.tenant_name || 'Lease')
    : tx.transaction_type === 'listing'
    ? `Asking ${formatCurrency(tx.asking_price)}`
    : 'Off Market';

  const subline = live
    ? [tx.price_per_sf ? `$${tx.price_per_sf.toFixed(0)}/SF asking` : null, 'On market - not a closed comp'].filter(Boolean).join(' · ')
    : tx.transaction_type === 'sale'
    ? [tx.price_per_sf ? `$${tx.price_per_sf.toFixed(0)}/SF` : null, tx.cap_rate ? `${tx.cap_rate.toFixed(2)}% Cap` : null].filter(Boolean).join(' · ')
    : tx.transaction_type === 'lease'
    ? [tx.lease_type, tx.rent_per_sf_year ? `$${tx.rent_per_sf_year.toFixed(2)}/SF/yr` : null].filter(Boolean).join(' · ')
    : tx.transaction_type === 'listing'
    ? [tx.asking_price_per_sf ? `$${tx.asking_price_per_sf.toFixed(0)}/SF` : null, tx.days_on_market ? `${tx.days_on_market} DOM` : null, tx.listing_status].filter(Boolean).join(' · ')
    : '';

  // Check which detail sections have data
  const hasSaleDetails = tx.transaction_type === 'sale' && (tx.noi || tx.buyer_name || tx.seller_name || tx.recording_date);
  const hasLeaseDetails = tx.transaction_type === 'lease' && (tx.lease_term_months || tx.lease_start_date || tx.lease_end_date || tx.rent_per_sf_year);
  const hasListingDetails = tx.transaction_type === 'listing' && (tx.asking_cap_rate || tx.days_on_market || tx.listing_status);
  const hasParties = tx.buyer_name || tx.seller_name || tx.broker_name || tx.broker_company;
  const hasFinancing = tx.lender || tx.loan_amount || tx.loan_type || tx.interest_rate || tx.maturity_date;
  const hasMeta = tx.source || tx.notes;
  const hasDetails = hasSaleDetails || hasLeaseDetails || hasListingDetails || hasParties || hasFinancing || hasMeta;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${expanded ? 'border-gray-300 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}>
      {/* Summary Row — always visible */}
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-5 py-4 text-left ${hasDetails ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${badge.color}`}>
            {badge.label}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{headline}</p>
            {subline && <p className="text-xs text-gray-500 mt-0.5">{subline}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0 ml-4">
          <span className="text-sm text-gray-500">
            {live ? 'No closing date' : formatDate(tx.transaction_date)}
          </span>
          {hasDetails && (
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </button>

      {/* Expanded Detail */}
      {expanded && hasDetails && (
        <div className="border-t bg-gray-50 px-5 py-4 space-y-5">
          {/* Sale Details */}
          {tx.transaction_type === 'sale' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {live ? 'Listing Details' : 'Sale Details'}
              </h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label={live ? 'Asking Price' : 'Sale Price'} value={formatCurrency(live ? liveAsk : tx.sale_price)} />
                <TxField label="Price/SF" value={tx.price_per_sf ? `$${tx.price_per_sf.toFixed(2)}` : null} />
                <TxField label="Cap Rate" value={tx.cap_rate ? `${tx.cap_rate.toFixed(2)}%` : null} />
                <TxField label="NOI" value={formatCurrency(tx.noi)} />
                <TxField label="Recording Date" value={formatDate(tx.recording_date)} />
              </dl>
            </div>
          )}

          {/* Lease Details */}
          {tx.transaction_type === 'lease' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Lease Details</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Tenant" value={tx.tenant_name} />
                <TxField label="Lease Type" value={tx.lease_type} />
                <TxField label="Rent/SF/Yr" value={tx.rent_per_sf_year ? `$${tx.rent_per_sf_year.toFixed(2)}` : null} />
                <TxField label="Term" value={tx.lease_term_months ? `${tx.lease_term_months} months` : null} />
                <TxField label="Start Date" value={formatDate(tx.lease_start_date)} />
                <TxField label="End Date" value={formatDate(tx.lease_end_date)} />
              </dl>
            </div>
          )}

          {/* Listing Details */}
          {tx.transaction_type === 'listing' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Listing Details</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Asking Price" value={formatCurrency(tx.asking_price)} />
                <TxField label="Asking $/SF" value={tx.asking_price_per_sf ? `$${tx.asking_price_per_sf.toFixed(0)}` : null} />
                <TxField label="Asking Cap" value={tx.asking_cap_rate ? `${tx.asking_cap_rate.toFixed(2)}%` : null} />
                <TxField label="Days on Market" value={tx.days_on_market?.toString()} />
                <TxField label="Status" value={tx.listing_status} />
              </dl>
            </div>
          )}

          {/* Parties */}
          {hasParties && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Parties</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Buyer" value={tx.buyer_name} />
                <TxField label="Seller" value={tx.seller_name} />
                <TxField label="Broker" value={tx.broker_name} />
                <TxField label="Brokerage" value={tx.broker_company} />
              </dl>
            </div>
          )}

          {/* Financing */}
          {hasFinancing && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Financing</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Lender" value={tx.lender} />
                <TxField label="Loan Amount" value={formatCurrency(tx.loan_amount)} />
                <TxField label="Loan Type" value={tx.loan_type} />
                <TxField label="Interest Rate" value={tx.interest_rate ? `${tx.interest_rate}%` : null} />
                <TxField label="Maturity" value={formatDate(tx.maturity_date)} />
                {tx.loan_amount && tx.sale_price ? (
                  <TxField label="LTV" value={`${((tx.loan_amount / tx.sale_price) * 100).toFixed(0)}%`} />
                ) : null}
              </dl>
            </div>
          )}

          {/* Source & Notes */}
          {hasMeta && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Source & Notes</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                <TxField label="Source" value={tx.source?.replace('_', ' ')} />
                {tx.notes && (
                  <div className="col-span-full">
                    <dt className="text-xs text-gray-500">Notes</dt>
                    <dd className="text-sm text-gray-700 mt-0.5">{tx.notes}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Inline field for transaction cards — only renders if value is present
function TxField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value || value === '-') return null;
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{value}</dd>
    </div>
  );
}

const ENTITY_TYPE_LABELS: Record<OwnerEntityType, string> = {
  individual: 'Individual',
  llc: 'LLC',
  trust: 'Trust',
  corporation: 'Corporation',
  reit: 'REIT',
  partnership: 'Partnership',
  government: 'Government',
  nonprofit: 'Nonprofit',
  unknown: 'Unknown',
};

const ENTITY_TYPE_COLORS: Record<OwnerEntityType, string> = {
  individual: 'bg-gray-100 text-gray-700',
  llc: 'bg-info-wash text-info',
  trust: 'bg-gray-100 text-gray-800',
  corporation: 'bg-info-wash text-info',
  reit: 'bg-success-wash text-success',
  partnership: 'bg-warning-wash text-warning',
  government: 'bg-red-100 text-red-700',
  nonprofit: 'bg-success-wash text-success',
  unknown: 'bg-gray-100 text-gray-500',
};

const SOURCE_COLORS: Record<ResearchSource, string> = {
  ai: 'bg-info-wash text-info',
  manual: 'bg-gray-100 text-gray-700',
  county_records: 'bg-warning-wash text-warning',
  other: 'bg-gray-100 text-gray-500',
};

const SOURCE_LABELS: Record<ResearchSource, string> = {
  ai: 'AI Research',
  manual: 'Manual',
  county_records: 'County Records',
  other: 'Other',
};

function OwnerResearchPanel({ propertyId }: { propertyId: string }) {
  const [research, setResearch] = useState<OwnerResearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    owner_name: '',
    owner_entity_type: 'unknown' as OwnerEntityType,
    registered_agent: '',
    mailing_address: '',
    phone: '',
    email: '',
    portfolio_estimate: '',
    research_notes: '',
  });
  const [error, setError] = useState<string | null>(null);

  const fetchResearch = useCallback(async () => {
    try {
      const data = await api.getOwnerResearch(propertyId);
      setResearch(data.research || []);
    } catch {
      // Silently fail — panel just shows empty
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchResearch();
  }, [fetchResearch]);

  const handleAIResearch = async () => {
    setAiLoading(true);
    setError(null);
    try {
      await api.runAIOwnerResearch(propertyId);
      await fetchResearch();
    } catch (err: any) {
      setError(err.message || 'AI research failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    setError(null);
    try {
      await api.createOwnerResearch(propertyId, {
        ...manualForm,
        portfolio_estimate: manualForm.portfolio_estimate ? parseInt(manualForm.portfolio_estimate, 10) : null,
      });
      setShowManualForm(false);
      setManualForm({
        owner_name: '',
        owner_entity_type: 'unknown',
        registered_agent: '',
        mailing_address: '',
        phone: '',
        email: '',
        portfolio_estimate: '',
        research_notes: '',
      });
      await fetchResearch();
    } catch (err: any) {
      setError(err.message || 'Failed to save research');
    }
  };

  const handleDelete = async (researchId: string) => {
    try {
      await api.deleteOwnerResearch(researchId);
      setResearch((prev) => prev.filter((r) => r.id !== researchId));
    } catch {
      // Silently fail
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle>Owner Research</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Owner Research{research.length > 0 ? ` (${research.length})` : ''}</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">Who's behind the LLC — AI + manual notes.</p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleAIResearch}
              disabled={aiLoading}
            >
              {aiLoading ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full mr-2" />
                  Researching…
                </>
              ) : (
                '✨ Research Owner'
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowManualForm(!showManualForm)}
            >
              {showManualForm ? 'Cancel' : 'Add Manual'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Manual Entry Form */}
        {showManualForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border space-y-3">
            <h4 className="text-sm font-semibold text-gray-700">New Manual Research Entry</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Owner Name</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-1.5 text-sm"
                  value={manualForm.owner_name}
                  onChange={(e) => setManualForm((f) => ({ ...f, owner_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Entity Type</label>
                <select
                  className="w-full border rounded-md px-3 py-1.5 text-sm"
                  value={manualForm.owner_entity_type}
                  onChange={(e) => setManualForm((f) => ({ ...f, owner_entity_type: e.target.value as OwnerEntityType }))}
                >
                  {Object.entries(ENTITY_TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Registered Agent</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-1.5 text-sm"
                  value={manualForm.registered_agent}
                  onChange={(e) => setManualForm((f) => ({ ...f, registered_agent: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Mailing Address</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-1.5 text-sm"
                  value={manualForm.mailing_address}
                  onChange={(e) => setManualForm((f) => ({ ...f, mailing_address: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Phone</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-1.5 text-sm"
                  value={manualForm.phone}
                  onChange={(e) => setManualForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full border rounded-md px-3 py-1.5 text-sm"
                  value={manualForm.email}
                  onChange={(e) => setManualForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Est. Portfolio Size</label>
                <input
                  type="number"
                  className="w-full border rounded-md px-3 py-1.5 text-sm"
                  value={manualForm.portfolio_estimate}
                  onChange={(e) => setManualForm((f) => ({ ...f, portfolio_estimate: e.target.value }))}
                  placeholder="# properties"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Research Notes</label>
              <textarea
                className="w-full border rounded-md px-3 py-1.5 text-sm"
                rows={3}
                value={manualForm.research_notes}
                onChange={(e) => setManualForm((f) => ({ ...f, research_notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={handleManualSubmit}>Save Entry</Button>
            </div>
          </div>
        )}

        {/* Research Timeline */}
        {research.length === 0 ? (
          <p className="text-xs text-gray-400 py-1">
            No research yet — run AI research on the owner, or add a manual entry.
          </p>
        ) : (
          <div className="space-y-4">
            {research.map((entry) => (
              <ResearchEntry key={entry.id} entry={entry} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResearchEntry({ entry, onDelete }: { entry: OwnerResearch; onDelete: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const rawData = entry.raw_data || {};
  const recommendations = rawData.outreach_recommendations || [];
  const redFlags = rawData.red_flags || [];
  const opportunities = rawData.opportunities || [];

  return (
    <div className="border rounded-lg p-4">
      {/* Header row */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SOURCE_COLORS[entry.research_source]}`}>
            {SOURCE_LABELS[entry.research_source]}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ENTITY_TYPE_COLORS[entry.owner_entity_type]}`}>
            {ENTITY_TYPE_LABELS[entry.owner_entity_type]}
          </span>
          {entry.portfolio_estimate != null && (
            <span className="text-xs text-gray-500">
              ~{entry.portfolio_estimate} properties
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{formatDate(entry.created_at)}</span>
          <button
            onClick={() => onDelete(entry.id)}
            className="text-gray-400 hover:text-red-500 text-xs"
            title="Delete"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Owner name */}
      {entry.owner_name && (
        <p className="text-sm font-semibold text-gray-900 mb-1">{entry.owner_name}</p>
      )}

      {/* AI Summary */}
      {entry.ai_summary && (
        <p className="text-sm text-gray-700 mb-2">{entry.ai_summary}</p>
      )}

      {/* Contact info for manual entries */}
      {(entry.mailing_address || entry.phone || entry.email || entry.registered_agent) && (
        <div className="flex gap-4 text-xs text-gray-500 mb-2 flex-wrap">
          {entry.registered_agent && <span>Agent: {entry.registered_agent}</span>}
          {entry.mailing_address && <span>Mailing: {entry.mailing_address}</span>}
          {entry.phone && <span>Phone: {entry.phone}</span>}
          {entry.email && <span>Email: {entry.email}</span>}
        </div>
      )}

      {/* Expandable analysis */}
      {(entry.research_notes || recommendations.length > 0 || redFlags.length > 0 || opportunities.length > 0) && (
        <>
          <button
            className="text-xs text-primary-500 hover:text-primary-600 font-medium"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Show details'}
          </button>

          {expanded && (
            <div className="mt-3 space-y-3">
              {/* Analysis text */}
              {entry.research_notes && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-600 mb-1">Analysis</h5>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.research_notes}</p>
                </div>
              )}

              {/* Outreach recommendations */}
              {recommendations.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-600 mb-1">Outreach Recommendations</h5>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {recommendations.map((rec: string, i: number) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Red flags */}
              {redFlags.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-red-600 mb-1">Red Flags</h5>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {redFlags.map((flag: string, i: number) => (
                      <li key={i}>{flag}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Opportunities */}
              {opportunities.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-success mb-1">Opportunities</h5>
                  <ul className="list-disc list-inside text-sm text-success space-y-1">
                    {opportunities.map((opp: string, i: number) => (
                      <li key={i}>{opp}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function MasterPropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [property, setProperty] = useState<PropertyData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingToDeal, setAddingToDeal] = useState(false);
  // NN0b: "Start a deal" - the CRM deal and its analyzer row together. The duplicate dialog
  // serves both this and "Add to My Deals", so it has to know which action it is answering.
  const [startingDeal, setStartingDeal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'my-deals' | 'deal'>('my-deals');
  // A5. "Add to My Deals" on a master property the user has already added is the easiest
  // duplicate in the app to create by accident - the button gives no sign it was ever pressed.
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[] | null>(null);
  // A5. Single-flight lock for the duplicate dialog's actions, acquired synchronously.
  // `busy` state cannot do this job: React batches updates, so two clicks in the same tick
  // both see it as false. See createSubmitLock's comment - this created two real rows.
  const submitLock = useRef(createSubmitLock());
  const [comps, setComps] = useState<PooledComp[]>([]);
  const [compsLoading, setCompsLoading] = useState(false);
  const [aiComps, setAiComps] = useState<any[] | null>(null);
  const [aiCompsBusy, setAiCompsBusy] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiSavedMsg, setAiSavedMsg] = useState<string | null>(null);
  const [valuation, setValuation] = useState<any | null>(null);
  const [valuationBusy, setValuationBusy] = useState(false);
  const [marketIntel, setMarketIntel] = useState<MarketIntelItem[]>([]);
  const [intelLoading, setIntelLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showMgmtModal, setShowMgmtModal] = useState(false);
  const [showLoiDialog, setShowLoiDialog] = useState(false);
  const [showLeaseProposal, setShowLeaseProposal] = useState(false);
  const [showProposalDialog, setShowProposalDialog] = useState(false);
  const [proposalFromChat, setProposalFromChat] = useState<PageActionFields>({});
  const [showBrochureDialog, setShowBrochureDialog] = useState(false);
  /** Built from the master property already on screen - no extra request. */
  // ⚠️ No `as any` casts: this page's `property` is typed `PropertyData`, which declares every
  // field used below. A cast here would hide exactly the kind of wrong-field-name error that
  // `asking_price` was on the analyzer page.
  const masterSubject: PageSubject | null = property
    ? {
        type: 'master_property',
        id: property.id,
        label: [property.address, property.city].filter(Boolean).join(', ') || 'Property',
        facts: [
          ...(property.county ? [{ label: 'County', value: property.county }] : []),
          ...(property.zoning ? [{ label: 'Zoning', value: property.zoning }] : []),
          ...(property.apn ? [{ label: 'APN', value: property.apn }] : []),
          ...(property.building_size ? [{ label: 'Building', value: `${Number(property.building_size).toLocaleString('en-US')} SF` }] : []),
        ],
      }
    : null;
  useSetPageSubject(masterSubject);
  /**
   * 🚨 WHAT THE RECORD CAN FILL IN, FETCHED - NOT hand-built from the row on this page.
   *
   * `GET /api/brochures/prefill/:id` returns the hero photo, the site plan, the AREA DEMOGRAPHICS
   * block and the suggested PROPERTY SUMMARY facts. It was built, tested and deployed, and then
   * **every page passed a hand-rolled seven-field object instead and never called it** - so the
   * whole "the record fills the brochure" feature reached nobody. Found by opening the dialog on
   * a real property and counting the pages: 3, meaning cover + contact + back and no content.
   */
  const [brochurePrefill, setBrochurePrefill] = useState<BrochurePrefill | null>(null);

  /** Fetch what the record can supply, then open. A failed fetch still opens with identity -
   *  losing the photos is a smaller harm than refusing to open the editor at all. */
  /**
   * ⛔⛔ THE RECORD'S PREFILL LOADS FIRST AND THE CHAT CHOICES GO ON TOP (the 99th) - the fetch
   * REPLACES the prefill, so merging before it resolved would discard what he said.
   */
  const openBrochure = async (fields?: PageActionFields): Promise<void> => {
    setShowBrochureDialog(true);
    if (!id) return;
    try {
      const res = await api.getBrochurePrefill(id);
      setBrochurePrefill(res.prefill as BrochurePrefill);
    } catch {
      setBrochurePrefill(null);
    }
    if (fields && Object.keys(fields).length) {
      setBrochurePrefill((prev) => ({ ...(prev ?? {}), ...fields }));
    }
  };

  /**
   * Terms the operator stated to the AI rail, for the LOI dialog below.
   *
   * ⚠️ THERE IS NO DEAL ON THIS PAGE, so no `loi-prefill` exists: the record supplies the address
   * and nothing about the offer. Price, EMD, seller and expiration are his to state or to type,
   * which is why what he says in chat is worth carrying rather than discarding.
   * ⛔ Cleared when the dialog closes - terms stated for one letter must not reappear on the next.
   */
  const [loiFromChat, setLoiFromChat] = useState<PageActionFields>({});

  /**
   * LP1.4 - the terms he stated in chat for a LEASE proposal.
   * ⛔ Cleared when the dialog closes, exactly as `loiFromChat` is: terms stated for one letter
   * must not reappear on the next.
   */
  const [leaseFromChat, setLeaseFromChat] = useState<PageActionFields>({});

  /** The AI rail's doors onto this page's own dialogs. Same functions the buttons call. */
  usePageAction('open_brochure_editor', (fields) => { void openBrochure(fields); });
  usePageAction('open_loi_dialog', (fields) => { setLoiFromChat(fields); setShowLoiDialog(true); });
  usePageAction('open_listing_proposal_dialog', (fields) => {
    setProposalFromChat(fields); setShowProposalDialog(true);
  });
  usePageAction('open_lease_proposal_dialog', (fields) => {
    setLeaseFromChat(fields);
    setShowLeaseProposal(true);
  });
  const [mgmtSaving, setMgmtSaving] = useState(false);
  const [mgmtForm, setMgmtForm] = useState({
    management_tier: 'asset_management' as ManagementTier,
    management_fee_percent: '',
    management_start_date: new Date().toISOString().split('T')[0],
    management_notes: '',
  });
  const [photos, setPhotos] = useState<Array<{ id: string; url: string; is_primary: boolean; photo_type: string; caption?: string }>>([]);
  /** OO2a: the Street View fallback for a record with no stored photo. `null` = not asked or
   *  no imagery there; a value = Google has a pano and this is its capture date (may be ''). */
  const [streetView, setStreetView] = useState<{ url: string; date: string } | null>(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [tenantRoll, setTenantRoll] = useState<any[]>([]);
  const [pipelineSaving, setPipelineSaving] = useState(false);

  // Stepper writes the canonical stage; clicking the current stage clears it.
  const handleSetPipeline = async (status: string) => {
    if (!id || pipelineSaving) return;
    setPipelineSaving(true);
    try {
      await api.updatePipelineStatus(id, status);
      setProperty(prev => (prev ? ({ ...prev, pipeline_status: status } as any) : prev));
    } catch (err) {
      console.error('Pipeline update failed:', err);
    } finally {
      setPipelineSaving(false);
    }
  };
  const [activePhotoId, setActivePhotoId] = useState<string | null>(null);
  const [tab, setTab] = useState<RecordTab>('overview');
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Not authenticated. Please log in.');
          return;
        }

        const apiBase = import.meta.env.VITE_API_URL || '/api';
        const response = await fetch(`${apiBase}/master-properties/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to fetch property');

        setProperty(data.property);
        setTransactions(data.transactions || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load property');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProperty();
  }, [id]);

  // Fetch pooled comps
  useEffect(() => {
    if (!id) return;
    setCompsLoading(true);
    api.getPooledComps(id)
      .then((data: any) => setComps(data.comps || []))
      .catch(() => {})
      .finally(() => setCompsLoading(false));
  }, [id]);

  // AI comp finder for THIS property (subject-scored ranking).
  const runAiComps = useCallback(async () => {
    if (!id) return;
    setAiCompsBusy(true); setAiSavedMsg(null);
    try {
      const res = await api.searchCompsAI({ subjectPropertyId: id, limit: 12 });
      setAiComps(res.comps || []);
    } catch (e) {
      console.error('AI comp search failed:', e);
      setAiComps([]);
    }
    setAiCompsBusy(false);
  }, [id]);

  const saveAiComps = useCallback(async () => {
    if (!id || !aiComps?.length) return;
    setAiSaving(true);
    try {
      const res: any = await api.bulkLinkComps(id, aiComps.map((c) => c.id));
      setAiSavedMsg(`Saved ${res?.linked ?? aiComps.length} comp${(res?.linked ?? aiComps.length) === 1 ? '' : 's'}`);
      api.getPooledComps(id).then((data: any) => setComps(data.comps || [])).catch(() => {});
    } catch (e) {
      console.error('Save comps failed:', e);
      setAiSavedMsg('Save failed');
    }
    setAiSaving(false);
  }, [id, aiComps]);

  const runValuation = useCallback(async () => {
    if (!id) return;
    setValuationBusy(true);
    try {
      const res: any = await api.compValuation({ subjectPropertyId: id, withNarrative: true });
      setValuation(res);
    } catch (e) {
      console.error('Valuation failed:', e);
      setValuation({ valuation: null, narrative: null, error: true });
    }
    setValuationBusy(false);
  }, [id]);

  // Lease roll: CoStar call-ready tenants linked to this building
  useEffect(() => {
    if (!id) return;
    api.getMapTenantProspects()
      .then((res: any) => setTenantRoll((res.tenants || []).filter((t: any) => t.master_property_id === id)))
      .catch(() => {});
  }, [id]);

  // Fetch property photos (hero + gallery)
  useEffect(() => {
    if (!id) return;
    api.getPhotos(id)
      .then((data: any) => setPhotos(data.photos || []))
      .catch(() => {});
  }, [id]);

  /**
   * OO2a: a record with NO stored photo falls back to live Street View.
   *
   * Measured 2026-09-03: of 7,748 master records, 3,555 carry a photo (2,282 real uploads,
   * 1,273 already-stored Street View) and 4,193 carry none, so the design's hero was hidden on
   * more than half the database. ⚠️ The backlog said only five records had photos - that was
   * measured against the wrong table and is wrong; photos DO hang off master records.
   *
   * ⭐⭐ THE METADATA CALL IS NOT OPTIONAL, AND IT IS FREE. The Street View STATIC endpoint
   * answers HTTP 200 with a gray "Sorry, we have no imagery here" tile when a location has no
   * pano - so `<img onError>` never fires and the page shows a broken-looking gray box instead
   * of nothing. `/imagery/street-metadata` is the only reliable test, and Google does not
   * charge for it. Ask first, render only on `OK`.
   *
   * Runs only when the record has loaded AND has no photos, so it costs nothing on the 3,555
   * records that already have one. Fails quiet: no fallback is the same as today.
   */
  useEffect(() => {
    if (!property || photos.length > 0) { setStreetView(null); return; }
    // ⚠️ `zip`, NOT `zip_code` - the column is `zip` and the page's own type says so; `zip_code`
    // type-checked clean and silently resolved to undefined. `mailing_zip` is the OWNER's
    // address and can be in another state, so it must never be used to locate the building.
    const address = [property.address, property.city, property.state, property.zip]
      .filter(Boolean).join(', ');
    if (!address) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    api.getStreetMetadata(address)
      .then(async (r: any) => {
        if (cancelled || r?.metadata?.status !== 'OK') return;
        // Only now is an image worth paying for: the metadata call said a pano exists.
        const url = await api.getStreetViewImage(address);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        objectUrl = url;
        setStreetView({ url, date: r.metadata.date || '' });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [property, photos.length]);

  // Fetch market intel for this property's area
  useEffect(() => {
    if (!property?.city) return;
    setIntelLoading(true);
    const params: Record<string, string> = { city: property.city };
    if (property.submarket) params.submarket = property.submarket;
    api.listMarketIntel(params)
      .then((data: any) => setMarketIntel(data.intel || []))
      .catch(() => {})
      .finally(() => setIntelLoading(false));
  }, [property?.city, property?.submarket]);

  const handleVerifyProperty = async () => {
    if (!id) return;
    setVerifying(true);
    try {
      await api.verifyProperty(id);
      setProperty(prev => prev ? { ...prev, freshness_status: 'current', verified_at: new Date().toISOString() } : null);
    } catch (err) {
      console.error('Verify failed:', err);
    } finally {
      setVerifying(false);
    }
  };

  /**
   * "Add to My Deals". The server answers 409 with candidates instead of inserting a second
   * row for a master property already in My Deals (A5), so a second click now prompts rather
   * than silently duplicating.
   */
  const handleAddToMyDeals = async (forceCreate?: boolean) => {
    setAddingToDeal(true);
    try {
      const result: any = await api.createPropertyFromMaster(id!, forceCreate);
      setDuplicates(null);
      const createdId = propertyIdFrom(result);
      if (createdId) {
        navigate(`/properties/${createdId}`);
      } else {
        // The create SUCCEEDED - this is only an unrecognised response shape. Silently doing
        // nothing left the user staring at an unchanged page with no idea whether it worked,
        // and the natural response to that is to click again and make a duplicate.
        toast.success('Added to My Deals', 'Open it from the My Deals list.');
      }
    } catch (err: any) {
      // Only a genuine 409 opens the dialog. A 503 (the duplicate check itself is down) and
      // every other failure stay a toast - a broken check must never become a one-click
      // duplicate.
      const candidates = duplicateCandidatesFrom(err);
      if (candidates) {
        setPendingAction('my-deals');
        setDuplicates(candidates);
        return;
      }
      setDuplicates(null);
      toast.error(friendlyErrorMessage(err, 'Failed to add to My Deals'));
    } finally {
      setAddingToDeal(false);
    }
  };

  /**
   * NN0b: start the CRM deal from this record - the analyzer row is created (or reused) with it,
   * so the deal page's strip is live on arrival. A 409 opens the same duplicate dialog as
   * "Add to My Deals"; `force_create` is passed only from the dialog's "Create separate".
   */
  const handleStartDeal = async (body: { master_property_id?: string; property_id?: string; force_create?: true }) => {
    setStartingDeal(true);
    try {
      const result: any = await api.startDealFromProperty(body);
      setDuplicates(null);
      const dealId = result?.deal?.id;
      if (dealId) {
        navigate(`/crm/deals/${dealId}`);
      } else {
        toast.success('Deal started', 'Open it from the Deals list.');
      }
    } catch (err: any) {
      const candidates = duplicateCandidatesFrom(err);
      if (candidates) {
        setPendingAction('deal');
        setDuplicates(candidates);
        return;
      }
      setDuplicates(null);
      toast.error(friendlyErrorMessage(err, 'Failed to start the deal'));
    } finally {
      setStartingDeal(false);
    }
  };

  /**
   * There is no document on this path, so `availableActions` offers only Open and Create
   * separate. `force_create` is passed from here and nowhere else.
   */
  const handleDuplicateChoice = async (action: DuplicateAction, c: DuplicateCandidate) => {
    if (pendingAction === 'deal') {
      // ⚠️ Synchronous lock before any await - two clicks in one tick would start two deals.
      if (!submitLock.current.tryAcquire()) return;
      try {
        if (action === 'open') await handleStartDeal({ property_id: c.id });
        else if (action === 'create_separate') await handleStartDeal({ master_property_id: id!, force_create: true });
      } finally {
        submitLock.current.release();
      }
      return;
    }
    if (action === 'open') {
      setDuplicates(null);
      navigate(`/properties/${c.id}`);
      return;
    }
    if (action !== 'create_separate') return;
    // ⚠️ Synchronous, before any await - see createSubmitLock. Two clicks in the same tick
    // would otherwise both reach handleAddToMyDeals and create two rows in My Deals.
    if (!submitLock.current.tryAcquire()) return;
    try {
      await handleAddToMyDeals(true);
    } finally {
      submitLock.current.release();
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
            <p className="text-gray-500 mt-2">Loading property...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !property) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button variant="outline" onClick={() => navigate('/data-hub')}>Back to Data Hub</Button>
          <div className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error || 'Property not found'}
          </div>
        </div>
      </Layout>
    );
  }

  const latestTx = latestClosedSale(transactions);

  // Section visibility checks
  const showIdentification = hasAnyValue(property, ['property_name', 'building_park', 'apn', 'costar_id', 'crexi_id', 'unit_suite']);
  const showClassification = hasAnyValue(property, ['building_class', 'building_status', 'zoning', 'property_subtype', 'submarket', 'market']);
  const showSizeStructure = hasAnyValue(property, ['building_size', 'land_area_sf', 'lot_size_acres', 'typical_floor_size', 'number_of_floors', 'number_of_units', 'number_of_buildings']);
  const showConstruction = hasAnyValue(property, ['year_built', 'month_built', 'year_renovated', 'month_renovated', 'construction_material']);
  const showIndustrial = hasAnyValue(property, ['clear_height_ft', 'dock_doors', 'grade_doors', 'rail_served', 'column_spacing', 'sprinkler_type', 'number_of_cranes', 'power', 'office_percentage', 'office_space', 'number_of_elevators']);
  const showParking = hasAnyValue(property, ['parking_spaces', 'parking_ratio']);
  const showLeasing = hasAnyValue(property, ['percent_leased', 'vacancy_percent', 'days_on_market', 'rent_per_sf', 'avg_weighted_rent']);
  const showLocation = hasAnyValue(property, ['cross_street', 'opportunity_zone', 'latitude', 'longitude']);
  const showPropertyDetails = showIdentification || showClassification || showSizeStructure || showConstruction || showIndustrial || showParking || showLeasing || showLocation;

  const ownerKeys: (keyof PropertyData)[] = ['owner_name', 'owner_contact', 'owner_address', 'owner_phone', 'mailing_city', 'mailing_state', 'mailing_zip', 'mailing_care_of', 'parent_company', 'fund_name'];
  const showOwner = hasAnyValue(property, ownerKeys);

  const mgmtKeys: (keyof PropertyData)[] = ['property_manager_name', 'property_manager_phone', 'leasing_company_name', 'leasing_company_contact', 'leasing_company_phone', 'developer_name', 'architect_name'];
  const showMgmt = hasAnyValue(property, mgmtKeys);

  const taxKeys: (keyof PropertyData)[] = ['improvement_value', 'land_value', 'total_parcel_value', 'parcel_value_type', 'tax_year', 'annual_tax_bill'];
  const showTax = hasAnyValue(property, taxKeys);

  const utilityKeys: (keyof PropertyData)[] = ['water', 'sewer', 'gas', 'amenities', 'features'];
  const showUtilities = hasAnyValue(property, utilityKeys);

  // OO2: what the header, the metrics strip and the deal-status block read.
  const pipeline = property.pipeline_status || 'untracked';
  const stageIdx = PIPELINE_STEPS.findIndex(s => s.includes.includes(pipeline));
  const listingPill = pipeline === 'listed'
    ? <Badge variant="primary" className="uppercase tracking-[.14em] text-[10px]">For Sale</Badge>
    : pipeline === 'under_contract'
      ? <Badge variant="info" className="uppercase tracking-[.12em] text-[10px]">Under contract</Badge>
      : pipeline === 'sold' || pipeline === 'leased'
        ? <Badge variant="dark" className="uppercase tracking-[.14em] text-[10px]">{pipeline === 'sold' ? 'Sold' : 'Leased'}</Badge>
        : null;
  const freshness = FRESHNESS_BADGE[property.freshness_status || 'unverified'] || FRESHNESS_BADGE.unverified;
  // CoStar writes "(Part of a 4 Property Sale)" INTO the address. The design shows the address
  // as the title and the portfolio note on the meta line, so split it for display only.
  const portfolioMatch = /^(.*?)\s*\((part of a (\d+)[- ]property sale|part of a portfolio)\)\s*$/i.exec(property.address || '');
  const titleAddress = portfolioMatch ? portfolioMatch[1] : property.address;
  const portfolioNote = portfolioMatch
    ? (portfolioMatch[3] ? `Part of a ${portfolioMatch[3]}-property sale` : 'Part of a portfolio sale')
    : (property.number_of_addresses != null && property.number_of_addresses > 1 ? `Part of a ${property.number_of_addresses}-property sale` : null);
  const cityLine = [[property.city, property.state].filter(Boolean).join(', '), property.zip].filter(Boolean).join(' ');
  const metaLine = [
    cityLine,
    property.county ? `${property.county} County` : null,
    property.submarket ? `${property.submarket} submarket` : null,
    property.apn ? `APN ${property.apn}` : null,
  ].filter(Boolean).join(' · ');
  const metrics: { k: string; v: string; unit?: string }[] = [
    { k: 'Building', v: property.building_size ? formatNumber(property.building_size) : '—', unit: property.building_size ? 'SF' : undefined },
    property.lot_size_acres != null
      ? { k: 'Lot', v: String(property.lot_size_acres), unit: 'AC' }
      : { k: 'Lot', v: property.land_area_sf ? formatNumber(property.land_area_sf) : '—', unit: property.land_area_sf ? 'SF' : undefined },
    { k: 'Year built', v: property.year_built ? String(property.year_built) : '—' },
    { k: 'Clear height', v: property.clear_height_ft ? String(property.clear_height_ft) : '—', unit: property.clear_height_ft ? 'ft' : undefined },
    { k: 'Doors', v: property.dock_doors != null || property.grade_doors != null ? `${property.dock_doors ?? 0} dock / ${property.grade_doors ?? 0} grade` : '—' },
    { k: 'Class', v: property.building_class || '—' },
  ];

  const latestSaleDate = latestTx?.transaction_date || latestTx?.recording_date || null;
  const linkCls = 'text-[11.5px] font-semibold leading-none text-primary-500 hover:text-primary-600 hover:underline underline-offset-[3px] disabled:opacity-50';

  return (
    <Layout>
      <div className="space-y-4">
        <Breadcrumbs items={[
          { label: 'Property Database', href: '/data-hub' },
          ...(property.market ? [{ label: property.market }] : []),
          { label: property.address || 'Property' },
        ]} />

        {/* ===== Header: the status pills, the Oswald title, one metadata line (OO2) ===== */}
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div className="min-w-0 flex-1 basis-[320px]">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {listingPill}
              {property.property_type && (
                <Badge variant="outline" className="uppercase tracking-[.12em] text-[10px]">
                  {[property.property_type, property.property_subtype].filter(Boolean).join(' · ')}
                </Badge>
              )}
              {(property as any).is_managed && (
                <Badge variant="success" className="uppercase tracking-[.12em] text-[10px]">Managed</Badge>
              )}
              <Badge variant={freshness.variant} className="uppercase tracking-[.12em] text-[10px]">{freshness.label}</Badge>
              {/* Freshness + verify - lives with the status, not the title */}
              <button
                type="button"
                onClick={handleVerifyProperty}
                disabled={verifying}
                className={linkCls}
                title={property.verified_at ? `Last verified: ${new Date(property.verified_at).toLocaleDateString()}` : 'Never verified'}
              >
                {verifying ? 'Verifying…' : property.verified_at ? `Verified ${new Date(property.verified_at).toLocaleDateString()} · re-verify` : 'Verify record'}
              </button>
            </div>
            <h1 className="break-words font-display text-[32px] font-semibold uppercase leading-[1.05] tracking-[-.01em] text-gray-900 sm:text-[38px]">
              {titleAddress}
            </h1>
            <div className="mt-[7px] text-[13.5px] leading-snug text-gray-700">
              {metaLine}
              {portfolioNote && <span className="text-gray-500">{metaLine ? ' · ' : ''}{portfolioNote}</span>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[minmax(0,1fr)_372px]">
          {/* ===================== LEFT COLUMN ===================== */}
          <div className="flex min-w-0 flex-col gap-[18px]">

            {/* OO2a: no stored photo, but Google has a pano here - the design's hero as ONE wide
                frame rather than the 2.1fr/1fr grid, because there is nothing to put in the two
                side cells and empty cells read as missing uploads. Labelled "Street View" in the
                same pill the stored street-view photos use, so it is never mistaken for a photo
                of the building someone took. */}
            {photos.length === 0 && streetView && (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="relative min-h-[240px] bg-gray-100 sm:min-h-[280px]">
                  <img
                    src={streetView.url}
                    alt={`Street view of ${property.address || 'this property'}`}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <span className="absolute bottom-3 left-3 rounded-sm bg-gray-900/70 px-2 py-1.5 text-[10px] font-semibold uppercase leading-none tracking-[.1em] text-white">
                    Street View{streetView.date ? ` · ${streetView.date}` : ''}
                  </span>
                </div>
              </div>
            )}

            {/* Photo gallery hero: the design's 2.1fr / 1fr grid. Real photos only - a record with
                none gets the Street View frame above, or no gallery at all. */}
            {photos.length > 0 && (() => {
              const defaultHero = photos.find(p => p.is_primary) || photos.find(p => p.photo_type === 'exterior') || photos[0];
              const hero = (activePhotoId && photos.find(p => p.id === activePhotoId)) || defaultHero;
              const others = photos.filter(p => p.id !== hero.id);
              const typeLabel = (p: { photo_type: string; url?: string }) =>
                p.url?.includes('/street-view/') ? 'Street View'
                  : p.photo_type === 'site_plan' ? 'Site Map'
                  : p.photo_type === 'aerial' ? 'Aerial'
                  : p.photo_type === 'floor_plan' ? 'Floor Plan'
                  : 'Exterior';
              const label = 'absolute bottom-3 left-3 rounded-sm bg-gray-900/70 px-2 py-1.5 text-[10px] font-semibold uppercase leading-none tracking-[.1em] text-white';
              return (
                <div>
                  {/* ⭐ ONE photo gets ONE WIDE FRAME, not the 2.1fr/1fr grid. The grid's two side
                      cells fall back to "No more photos", and two empty gray cells beside a single
                      photo read as missing uploads - the same reason the OO2a Street View fallback
                      is one frame. The OO2b backfill made this the COMMON case: it gives a
                      photo-less record exactly one photo, which moved thousands of records out of
                      the clean fallback and into the grid's empty state. */}
                  <div className={`grid grid-cols-1 gap-1.5 overflow-hidden rounded-xl border border-gray-200 bg-white ${
                    photos.length > 1 ? 'sm:grid-cols-[2.1fr_1fr] sm:grid-rows-[132px_132px]' : ''
                  }`}>
                    <div className={`relative bg-gray-100 ${photos.length > 1 ? 'min-h-[240px] sm:row-span-2 sm:min-h-0' : 'min-h-[240px] sm:min-h-[280px]'}`}>
                      <img src={hero.url} alt={property.address || 'Property hero'} className="absolute inset-0 h-full w-full object-cover" />
                      <span className={label}>{typeLabel(hero)}</span>
                    </div>
                    {photos.length > 1 && [0, 1].map(i => {
                      const p = others[i];
                      return (
                        <div key={p ? p.id : `empty-${i}`} className="relative hidden min-h-0 bg-gray-100 sm:block">
                          {p ? (
                            <button type="button" onClick={() => setActivePhotoId(p.id)} className="block h-full w-full" title="Show this photo">
                              <img src={p.url} alt="" className="h-full w-full object-cover" />
                              <span className={label}>{typeLabel(p)}</span>
                            </button>
                          ) : (
                            <div className="flex h-full items-center justify-center text-[11px] text-gray-400">No more photos</div>
                          )}
                          {i === 1 && photos.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setShowAllPhotos(v => !v)}
                              className="absolute bottom-2.5 right-2.5 h-[30px] rounded-lg bg-gray-900/80 px-[11px] text-[11.5px] font-semibold leading-none text-white hover:bg-gray-900"
                            >
                              {showAllPhotos ? 'Hide photos' : `View all ${photos.length} photos`}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(showAllPhotos || photos.length === 2) && photos.length > 1 && (
                    <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                      {photos.map(p => (
                        <button key={p.id} type="button" onClick={() => setActivePhotoId(p.id)} className="flex shrink-0 flex-col items-center">
                          <img
                            src={p.url}
                            alt=""
                            className={`h-16 w-24 rounded-lg object-cover transition-opacity ${p.id === hero.id ? 'ring-2 ring-primary-500' : 'opacity-75 hover:opacity-100'}`}
                          />
                          <span className="mt-1 text-[10px] text-gray-500">{typeLabel(p)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* The record card: the design's tabs, the 6-up metrics strip, then the tab's content.
                The tabs are REAL - each one shows what the page used to stack in a long scroll. */}
            <Card>
              <div role="tablist" aria-label="Property record" className="flex gap-0.5 overflow-x-auto border-b border-gray-200 px-4">
                {RECORD_TABS.map((t) => {
                  const active = tab === t.key;
                  const count = t.key === 'financials' ? transactions.length
                    : t.key === 'comps' ? comps.length
                    : t.key === 'tenants' ? tenantRoll.length
                    : t.key === 'intel' ? marketIntel.length
                    : 0;
                  return (
                    <button
                      key={t.key}
                      role="tab"
                      type="button"
                      aria-selected={active}
                      onClick={() => setTab(t.key)}
                      className={`-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-3.5 text-[13px] leading-none transition-colors ${
                        active ? 'border-primary-500 font-semibold text-gray-900' : 'border-transparent font-medium text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      {t.label}
                      {count > 0 && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">{count}</span>}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 border-b border-gray-200 sm:grid-cols-3 lg:grid-cols-6">
                {metrics.map((m, i) => (
                  <div key={m.k} className={`border-gray-150 px-[18px] py-4 ${i % 2 === 0 ? 'border-r' : ''} ${i < 4 ? 'border-b' : ''} sm:border-b-0 ${i % 3 !== 2 ? 'sm:border-r' : 'sm:border-r-0'} ${i < 3 ? 'sm:border-b' : ''} lg:border-b-0 ${i < 5 ? 'lg:border-r' : 'lg:border-r-0'}`}>
                    <div className="mb-2 text-[9.5px] font-bold uppercase leading-none tracking-[.13em] text-gray-400">{m.k}</div>
                    <div className="font-display text-[19px] font-medium leading-none tabular-nums text-gray-900">
                      {m.v}
                      {m.unit && <span className="ml-1 font-sans text-[12px] font-normal text-gray-500">{m.unit}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* ---- Overview: the property record ---- */}
              {tab === 'overview' && (
                <div className="px-[22px] pb-6 pt-5">
                  <div className="mb-1 flex flex-wrap items-baseline gap-2.5">
                    <h2 className="text-[15px] font-semibold leading-none text-gray-900">Property record</h2>
                    <span className="text-[11.5px] text-gray-400">Specs merge from CoStar, county data &amp; imports</span>
                  </div>
                  {!showPropertyDetails && !showOwner && !showMgmt && !showTax && !showUtilities && (
                    <p className="mt-3 text-[12.5px] text-gray-500">No specs on file yet - CoStar and county imports fill this in.</p>
                  )}
                  {(showIdentification || showClassification) && (
                    <RecordSection title="Identification & classification">
                      <Field label="Property name" value={property.property_name} />
                      <Field label="Building park" value={property.building_park} />
                      <Field label="APN" value={property.apn} />
                      <Field label="Zoning" value={property.zoning} />
                      <Field label="Property subtype" value={property.property_subtype} />
                      <Field label="Building class" value={property.building_class} />
                      <Field label="Building status" value={property.building_status} />
                      <Field label="Submarket" value={property.submarket} />
                      <Field label="Market" value={property.market} />
                      <Field label="CoStar ID" value={property.costar_id} />
                      <Field label="Crexi ID" value={property.crexi_id} />
                      <Field label="Unit / suite" value={property.unit_suite} />
                    </RecordSection>
                  )}
                  {/* FZ4: what could be built here - land only, from the stored answer. */}
                  {property.property_type === 'land' && <WhatCouldBeBuilt propertyId={property.id} />}
                  {/* ZS2: the rule book, on the parcel. FZ2: the zone read from the MAP when there is one -
                      CoStar's text only where nobody has resolved the parcel yet. Renders nothing without a city. */}
                  <ZoningPanel
                    city={property.zoning_jurisdiction ?? property.city}
                    zoning={property.zoning_source ? property.zoning_normalized ?? property.zoning : property.zoning}
                    mapped={!!property.zoning_source}
                  />
                  {/* DOC1a, his ruling 2026-09-04: "yes, property database records should hold
                      documents too." Its own filings, plus those on any My Deals property created
                      from it - resolved through `additional_data.master_property_id`, which the
                      app already writes, so nothing is copied and nothing is guessed. */}
                  <PropertyDocumentsPanel kind="master" propertyId={property.id} />
                  {showSizeStructure && (
                    <RecordSection title="Size & structure">
                      <Field label="Building size" value={property.building_size ? `${formatNumber(property.building_size)} SF` : undefined} />
                      <Field label="Land area" value={property.land_area_sf ? `${formatNumber(property.land_area_sf)} SF` : undefined} />
                      <Field label="Lot size" value={property.lot_size_acres != null ? `${property.lot_size_acres} AC` : undefined} />
                      <Field label="Typical floor" value={property.typical_floor_size ? `${formatNumber(property.typical_floor_size)} SF` : undefined} />
                      <Field label="Floors" value={property.number_of_floors} />
                      <Field label="Units" value={property.number_of_units} />
                      <Field label="Buildings" value={property.number_of_buildings} />
                      <Field label="Coverage" value={property.building_size && property.land_area_sf ? `${Math.round((property.building_size / property.land_area_sf) * 100)}%` : undefined} />
                    </RecordSection>
                  )}
                  {showConstruction && (
                    <RecordSection title="Construction">
                      <Field label="Year built" value={property.year_built} />
                      <Field label="Month built" value={property.month_built} />
                      <Field label="Year renovated" value={property.year_renovated} />
                      <Field label="Month renovated" value={property.month_renovated} />
                      <Field label="Construction" value={property.construction_material} />
                    </RecordSection>
                  )}
                  {showIndustrial && (
                    <RecordSection title="Industrial features">
                      <Field label="Clear height" value={property.clear_height_ft ? `${property.clear_height_ft} ft` : undefined} />
                      <Field label="Dock doors" value={property.dock_doors} />
                      <Field label="Grade doors" value={property.grade_doors} />
                      <Field label="Rail served" value={property.rail_served} />
                      <Field label="Column spacing" value={property.column_spacing} />
                      <Field label="Sprinklers" value={property.sprinkler_type} />
                      <Field label="Cranes" value={property.number_of_cranes} />
                      <Field label="Power" value={property.power} />
                      <Field label="Office" value={property.office_percentage != null ? `${property.office_percentage}%` : undefined} />
                      <Field label="Office space" value={property.office_space ? `${formatNumber(property.office_space)} SF` : undefined} />
                      <Field label="Elevators" value={property.number_of_elevators} />
                    </RecordSection>
                  )}
                  {showParking && (
                    <RecordSection title="Parking">
                      <Field label="Parking spaces" value={property.parking_spaces} />
                      <Field label="Parking ratio" value={property.parking_ratio} />
                    </RecordSection>
                  )}
                  {showLeasing && (
                    <RecordSection title="Leasing">
                      <Field label="Leased" value={property.percent_leased != null ? `${property.percent_leased}%` : undefined} />
                      <Field label="Vacancy" value={property.vacancy_percent != null ? `${property.vacancy_percent}%` : undefined} />
                      <Field label="Days on market" value={property.days_on_market} />
                      <Field label="Rent / SF" value={property.rent_per_sf != null ? `$${property.rent_per_sf.toFixed(2)}` : undefined} />
                      <Field label="Avg weighted rent" value={property.avg_weighted_rent != null ? `$${property.avg_weighted_rent.toFixed(2)}` : undefined} />
                    </RecordSection>
                  )}
                  {showLocation && (
                    <RecordSection title="Location">
                      <Field label="Cross street" value={property.cross_street} />
                      <Field label="Opportunity zone" value={property.opportunity_zone} />
                      <Field label="Latitude" value={property.latitude} />
                      <Field label="Longitude" value={property.longitude} />
                    </RecordSection>
                  )}
                  {showOwner && (
                    <RecordSection title="Owner & contacts">
                      <Field label="Owner" value={property.owner_name} />
                      <Field label="Owner contact" value={property.owner_contact} />
                      <Field label="Owner phone" value={property.owner_phone} />
                      <Field label="Owner address" value={property.owner_address} />
                      <Field label="Mailing city" value={property.mailing_city} />
                      <Field label="Mailing state" value={property.mailing_state} />
                      <Field label="Mailing zip" value={property.mailing_zip} />
                      <Field label="Care of" value={property.mailing_care_of} />
                      <Field label="Parent company" value={property.parent_company} />
                      <Field label="Fund" value={property.fund_name} />
                    </RecordSection>
                  )}
                  {showMgmt && (
                    <RecordSection title="Management & leasing">
                      <Field label="Property manager" value={property.property_manager_name} />
                      <Field label="Manager phone" value={property.property_manager_phone} />
                      <Field label="Leasing company" value={property.leasing_company_name} />
                      <Field label="Leasing contact" value={property.leasing_company_contact} />
                      <Field label="Leasing phone" value={property.leasing_company_phone} />
                      <Field label="Developer" value={property.developer_name} />
                      <Field label="Architect" value={property.architect_name} />
                    </RecordSection>
                  )}
                  {showTax && (
                    <RecordSection title="Tax & valuation">
                      <Field label="Improvement value" value={property.improvement_value != null ? formatCurrency(property.improvement_value) : undefined} />
                      <Field label="Land value" value={property.land_value != null ? formatCurrency(property.land_value) : undefined} />
                      <Field label="Total parcel value" value={property.total_parcel_value != null ? formatCurrency(property.total_parcel_value) : undefined} />
                      <Field label="Value type" value={property.parcel_value_type} />
                      <Field label="Tax year" value={property.tax_year} />
                      <Field label="Annual tax bill" value={property.annual_tax_bill != null ? formatCurrency(property.annual_tax_bill) : undefined} />
                    </RecordSection>
                  )}
                  {showUtilities && (
                    <RecordSection title="Utilities & amenities">
                      <Field label="Water" value={property.water} />
                      <Field label="Sewer" value={property.sewer} />
                      <Field label="Gas" value={property.gas} />
                      <Field label="Amenities" value={property.amenities} />
                      <Field label="Features" value={property.features} />
                    </RecordSection>
                  )}
                </div>
              )}

              {/* ---- Financials: the estimate and the transactions of record ---- */}
              {tab === 'financials' && (
                <div className="space-y-6 px-[22px] pb-6 pt-5">
                  <div>
                    <div className="mb-3 flex flex-wrap items-center gap-2.5">
                      <h2 className="text-[15px] font-semibold leading-none text-gray-900">Estimated value</h2>
                      <span className="text-[11.5px] text-gray-400">Comp-adjusted range and AI summary, not an appraisal</span>
                      <Button variant="outline" size="sm" className="ml-auto" onClick={runValuation} disabled={valuationBusy}>
                        {valuationBusy ? 'Analyzing…' : valuation ? 'Refresh' : 'Estimate value'}
                      </Button>
                    </div>
                    {!valuation ? (
                      <p className="text-[12.5px] text-gray-500">Not estimated yet.</p>
                    ) : valuation.error ? (
                      <p className="text-[12.5px] text-primary-500">Couldn't generate a valuation right now.</p>
                    ) : !valuation.valuation ? (
                      <p className="text-[12.5px] text-gray-500">Not enough comparable sales to estimate a value for this property yet.</p>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 items-end gap-3 text-center">
                          <div><p className="text-[9.5px] font-bold uppercase tracking-[.13em] text-gray-400">Low</p><p className="mt-1.5 font-display text-[19px] font-medium tabular-nums text-gray-700">{formatCurrency(valuation.valuation.low)}</p></div>
                          <div className="rounded-lg bg-gray-900 py-2.5 text-white"><p className="text-[9.5px] font-bold uppercase tracking-[.13em] text-primary-400">Midpoint</p><p className="mt-1.5 font-display text-[26px] font-semibold tabular-nums">{formatCurrency(valuation.valuation.mid)}</p></div>
                          <div><p className="text-[9.5px] font-bold uppercase tracking-[.13em] text-gray-400">High</p><p className="mt-1.5 font-display text-[19px] font-medium tabular-nums text-gray-700">{formatCurrency(valuation.valuation.high)}</p></div>
                        </div>
                        <p className="text-center text-[11.5px] text-gray-500">
                          ${valuation.valuation.psfLow}-${valuation.valuation.psfHigh}/SF · based on {valuation.valuation.basisCount} comparable sales
                        </p>
                        {valuation.narrative && (
                          <div className="whitespace-pre-wrap border-t border-gray-150 pt-3 text-[13px] leading-relaxed text-gray-700">
                            {valuation.narrative.replace(/\*\*/g, '')}
                          </div>
                        )}
                        <p className="text-[11px] text-gray-400">Estimate for internal analysis only. Not a formal appraisal or opinion of value.</p>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-150 pt-5">
                    <div className="mb-3 flex flex-wrap items-baseline gap-2.5">
                      <h2 className="text-[15px] font-semibold leading-none text-gray-900">Transaction history</h2>
                      <span className="text-[11.5px] text-gray-400">Sales and leases of record</span>
                    </div>
                    {transactions.length === 0 ? (
                      <p className="text-[12.5px] text-gray-500">No sales or leases recorded yet - CoStar imports fill this automatically.</p>
                    ) : (
                      <div className="space-y-3">
                        {transactions.map((tx) => (
                          <TransactionCard key={tx.id} tx={tx} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ---- Comps: the design's "Sale comparables" table, plus the AI finder ---- */}
              {tab === 'comps' && (
                <div>
                  <div className="flex flex-wrap items-center gap-2.5 px-5 py-4">
                    <h2 className="text-[15px] font-semibold leading-none text-gray-900">Sale comparables</h2>
                    <span className="text-[11.5px] text-gray-400">Linked comparable sales, AI-ranked or hand-picked</span>
                    <Button variant="outline" size="sm" className="ml-auto" onClick={runAiComps} disabled={aiCompsBusy}>
                      {aiCompsBusy ? 'Finding…' : 'AI comps'}
                    </Button>
                  </div>
                  {compsLoading ? (
                    <p className="px-5 pb-5 text-[12.5px] text-gray-500">Loading comps...</p>
                  ) : comps.length === 0 ? (
                    <p className="px-5 pb-5 text-[12.5px] text-gray-500">
                      No comps linked yet - run AI comps above, or{' '}
                      <Link to={`/comp-search?subjectId=${id}`}>search comps</Link>.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <div className="min-w-[640px]">
                        <div className="grid grid-cols-[2.2fr_1fr_1fr_.9fr_.9fr_.7fr] border-y border-gray-150 bg-gray-50 px-5 py-2.5 text-[9.5px] font-bold uppercase leading-none tracking-[.12em] text-gray-500">
                          <div>Address</div><div className="text-right">Price</div><div className="text-right">$/SF</div><div className="text-right">Cap</div><div className="text-right">Closed</div><div className="text-right">Match</div>
                        </div>
                        {comps.map(comp => (
                          <div key={comp.id} className="grid grid-cols-[2.2fr_1fr_1fr_.9fr_.9fr_.7fr] items-center border-b border-gray-100 px-5 py-3 text-[13px] font-medium leading-tight tabular-nums text-gray-800 last:border-b-0 hover:bg-gray-50">
                            <div className="min-w-0">
                              {comp.comp_property_id ? (
                                <Link to={`/data-hub/${comp.comp_property_id}`} className="block truncate text-gray-800 hover:text-primary-500">{comp.address || '—'}</Link>
                              ) : (<span className="block truncate">{comp.address || '—'}</span>)}
                              <div className="text-[11px] font-normal text-gray-400">{[comp.city, comp.distance_miles != null ? `${comp.distance_miles.toFixed(1)} mi` : null, comp.source].filter(Boolean).join(' · ') || '—'}</div>
                            </div>
                            <div className="text-right">{comp.sale_price ? formatCurrency(comp.sale_price) : '—'}</div>
                            <div className="text-right">{comp.price_per_sf ? `$${comp.price_per_sf.toFixed(0)}` : '—'}</div>
                            <div className="text-right">{comp.cap_rate ? `${comp.cap_rate.toFixed(2)}%` : '—'}</div>
                            <div className="text-right text-gray-700">{displayDate(comp.sale_date, { month: 'short', year: 'numeric' }) ?? '—'}</div>
                            <div className="text-right text-gray-700">{comp.similarity_score != null ? `${(comp.similarity_score * 100).toFixed(0)}%` : '—'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI-ranked comparables */}
                  {aiComps && (
                    <div className="border-t border-gray-150 px-5 pb-5 pt-4">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <p className="text-[13px] font-semibold text-gray-900">AI-ranked comparables{aiComps.length > 0 ? ` (${aiComps.length})` : ''}</p>
                        {aiComps.length > 0 && (
                          <div className="ml-auto flex items-center gap-2">
                            {aiSavedMsg && <span className="text-[11.5px] text-success">{aiSavedMsg}</span>}
                            <Button variant="outline" size="sm" onClick={saveAiComps} disabled={aiSaving}>
                              {aiSaving ? 'Saving…' : 'Save all as comps'}
                            </Button>
                          </div>
                        )}
                      </div>
                      {aiComps.length === 0 ? (
                        <p className="py-2 text-[12.5px] text-gray-500">No comparable properties found for this one yet.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <div className="min-w-[640px]">
                            <div className="grid grid-cols-[.6fr_2.2fr_1fr_.9fr_.9fr_.7fr] border-y border-gray-150 bg-gray-50 px-3 py-2.5 text-[9.5px] font-bold uppercase leading-none tracking-[.12em] text-gray-500">
                              <div>Match</div><div>Address</div><div className="text-right">Price</div><div className="text-right">$/SF</div><div className="text-right">Size</div><div className="text-right">Dist</div>
                            </div>
                            {aiComps.map((c: any) => (
                              <div key={c.id} className="grid grid-cols-[.6fr_2.2fr_1fr_.9fr_.9fr_.7fr] items-center border-b border-gray-100 px-3 py-3 text-[13px] font-medium leading-tight tabular-nums text-gray-800 last:border-b-0 hover:bg-gray-50">
                                <div><span className="rounded-sm bg-gray-900 px-1.5 py-1 text-[10px] font-bold text-white">{c.score}%</span></div>
                                <div className="min-w-0">
                                  <Link to={`/data-hub/${c.id}`} className="block truncate text-gray-800 hover:text-primary-500">{c.address || '—'}</Link>
                                  <div className="text-[11px] font-normal text-gray-400">{c.city || '—'}</div>
                                </div>
                                <div className="text-right">{c.price ? formatCurrency(c.price) : '—'}</div>
                                <div className="text-right">{c.price && c.building_size ? `$${Math.round(c.price / c.building_size)}` : '—'}</div>
                                <div className="text-right text-gray-700">{c.building_size ? c.building_size.toLocaleString() : '—'}</div>
                                <div className="text-right text-gray-700">{c.breakdown?.distance_mi != null ? `${c.breakdown.distance_mi.toFixed(1)} mi` : '—'}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ---- Tenants: the lease roll ---- */}
              {tab === 'tenants' && (
                <div className="px-5 pb-5 pt-4">
                  <div className="mb-3 flex flex-wrap items-baseline gap-2.5">
                    <h2 className="text-[15px] font-semibold leading-none text-gray-900">Lease roll</h2>
                    <span className="text-[11.5px] text-gray-400">Tenants and lease expirations, colored by urgency</span>
                  </div>
                  {tenantRoll.length === 0 ? (
                    <p className="text-[12.5px] text-gray-500">No tenants on file for this building.</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {tenantRoll.map((t: any) => (
                        <div key={t.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: tenantExpiryColor(t.lease_expiration) }} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13.5px] font-semibold text-gray-800">{t.tenant_name}</p>
                            <p className="truncate text-[11.5px] text-gray-500">
                              {[t.industry, t.contact_name, t.phone].filter(Boolean).join(' · ') || '—'}
                            </p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-[13px] font-semibold tabular-nums text-gray-800">
                              {displayDate(t.lease_expiration, { month: 'short', year: 'numeric' }) ?? 'No exp. on file'}
                            </p>
                            {t.sf_occupied && <p className="text-[11px] tabular-nums text-gray-400">{Number(t.sf_occupied).toLocaleString()} SF</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ---- Intel: news and signals for the trade area ---- */}
              {tab === 'intel' && (
                <div className="px-5 pb-5 pt-4">
                  <div className="mb-3 flex flex-wrap items-center gap-2.5">
                    <h2 className="text-[15px] font-semibold leading-none text-gray-900">Market intel</h2>
                    <span className="text-[11.5px] text-gray-400">News and signals for this trade area</span>
                    <Link to="/market-intel" className="ml-auto text-[11.5px] font-semibold">View all intel</Link>
                  </div>
                  {intelLoading ? (
                    <p className="text-[12.5px] text-gray-500">Loading market intel...</p>
                  ) : marketIntel.length === 0 ? (
                    <p className="text-[12.5px] text-gray-500">
                      No market intel for this area yet -{' '}
                      <Link to="/market-intel">add intelligence</Link>.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {marketIntel.slice(0, 5).map(item => (
                        <div key={item.id} className="rounded-lg border border-gray-150 p-3">
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <Badge variant={IMPACT_VARIANT[item.impact] || 'default'}>{item.impact}</Badge>
                            <Badge>{item.intel_type.replace(/_/g, ' ')}</Badge>
                            {item.effective_date && (
                              <span className="text-[11px] text-gray-400">{displayDate(item.effective_date)}</span>
                            )}
                          </div>
                          <h4 className="text-[13.5px] font-semibold text-gray-900">{item.title}</h4>
                          <p className="mt-0.5 line-clamp-2 text-[13px] text-gray-700">{item.description}</p>
                          {item.source && <p className="mt-1 text-[11px] text-gray-400">Source: {item.source}</p>}
                        </div>
                      ))}
                      {marketIntel.length > 5 && (
                        <Link to="/market-intel" className="block text-center text-[12.5px] font-semibold">
                          View all {marketIntel.length} intel entries
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Panels that render their own card, shown with the tab they belong to. Mounted only
                while their tab is active, so their requests fire only when they are looked at. */}
            {tab === 'financials' && <PropertyLoanPanel propertyId={property.id} />}
            {tab === 'intel' && property.latitude != null && property.longitude != null && (
              <DemographicsPanel
                lat={property.latitude}
                lng={property.longitude}
                subject={{ label: property.address || 'Subject Property', lat: property.latitude, lng: property.longitude, isProperty: true }}
                masterPropertyId={property.id}
              />
            )}
            {tab === 'intel' && <OwnerResearchPanel propertyId={property.id} />}
          </div>

          {/* ===================== RIGHT COLUMN ===================== */}
          {/*
            🚨 `order-first lg:order-none` - PG2, HIS ASK 2026-09-04: "those buttons that are at the
            bottom, I feel like we should put those to the top."

            They were never "at the bottom" by design. This grid is
            `lg:grid-cols-[minmax(0,1fr)_372px]`, so on a desktop this column is the 372px RIGHT
            RAIL beside the content. Below `lg` the grid collapses to ONE column and the rail,
            being second in the DOM, lands after every detail card on the page - so "+ Add to My
            Deals", Find Comps and Generate LOI sat past a full screen of scrolling on a phone.

            ⭐ `lg:order-none` is load-bearing: without it the rail jumps to the LEFT of the
            content on his desktop, which trades his bug for a worse one.
          */}
          <div className="flex flex-col gap-3.5 order-first lg:order-none">
            {/* Latest sale + the one red action, the outline grid, the links (the design's facts / action card) */}
            <Card>
              <div className="border-b border-gray-150 px-5 pb-4 pt-[18px]">
                <div className="mb-[9px] text-[9.5px] font-bold uppercase leading-none tracking-[.14em] text-gray-400">
                  {latestTx?.sale_price
                    ? `Latest sale${latestSaleDate ? ` · ${displayDate(latestSaleDate, { month: 'short', year: 'numeric' })}` : ''}`
                    : 'Latest sale'}
                </div>
                <div className="flex flex-wrap items-end gap-[9px]">
                  <div className="font-display text-[31px] font-semibold leading-none tabular-nums text-gray-900">
                    {latestTx?.sale_price ? formatCurrency(latestTx.sale_price) : '—'}
                  </div>
                  {latestTx?.price_per_sf != null && (
                    <div className="pb-0.5 text-[13px] font-medium leading-none text-gray-700">${Math.round(latestTx.price_per_sf)}/SF</div>
                  )}
                </div>
                {!latestTx?.sale_price && <p className="mt-1.5 text-[11.5px] text-gray-400">No sale on record.</p>}
                <div className="mt-3.5 flex flex-wrap gap-4 border-t border-dashed border-gray-200 pt-3.5">
                  {[
                    ['Cap rate', latestTx?.cap_rate ? `${latestTx.cap_rate.toFixed(2)}%` : '—'],
                    ['Leased', property.percent_leased != null ? `${property.percent_leased}%` : '—'],
                    ['Source', latestTx?.source || property.source || '—'],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="mb-1 text-[10.5px] leading-tight text-gray-500">{k}</div>
                      <div className="text-[13px] font-semibold leading-none text-gray-800">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2 px-5 pb-5 pt-4">
                {/* One red primary per view; everything else stays quiet */}
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => handleAddToMyDeals()}
                  disabled={addingToDeal}
                  isLoading={addingToDeal}
                >
                  + Add to My Deals
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { void handleStartDeal({ master_property_id: id! }); }}
                    disabled={startingDeal || addingToDeal}
                    isLoading={startingDeal}
                  >
                    Start a deal
                  </Button>
                  <Link to={`/comp-search?subjectId=${id}`} className="contents">
                    <Button variant="outline" className="w-full">Find Comps</Button>
                  </Link>
                  <Button variant="outline" onClick={() => setShowLoiDialog(true)}>Generate LOI</Button>
                  <Button variant="outline" onClick={() => setShowLeaseProposal(true)}>Lease Proposal</Button>
                  <Button variant="outline" onClick={() => { void openBrochure(); }}>Brochure / OM</Button>
                  {/* WT1: a walk-through of THIS building - the start screen skips its search. */}
                  <Link to={`/tours/new?property=${id}`} className="contents">
                    <Button variant="outline" className="w-full">Walk-through</Button>
                  </Link>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3.5 gap-y-2">
                  <button type="button" onClick={() => setShowProposalDialog(true)} className={linkCls}>Listing proposal</button>
                  <CallPrepButton propertyId={id} variant="link" />
                  {(property as any).is_managed ? (
                    <>
                      <Link to={`/asset-services/${id}`} className={linkCls}>View in Asset Services</Link>
                      <button
                        type="button"
                        className={linkCls}
                        onClick={async () => {
                          if (!confirm('Disable management for this property?')) return;
                          try {
                            await api.updatePropertyManagement(id!, { is_managed: false, management_tier: null });
                            setProperty(prev => prev ? { ...prev, is_managed: false, management_tier: null } as any : null);
                          } catch (err) { console.error(err); }
                        }}
                      >
                        Disable management
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setShowMgmtModal(true)} className={linkCls}>Enable management</button>
                  )}
                </div>
              </div>
            </Card>

            {/* The map card: the location, its submarket, expandable to full screen */}
            {property.latitude != null && property.longitude != null && (
              <Card>
                <div className="relative h-[196px]">
                  <MapContainer
                    center={[property.latitude!, property.longitude!]}
                    zoom={15}
                    className="h-full w-full"
                    scrollWheelZoom={false}
                    attributionControl={false}
                  >
                    <TileLayer {...STREET_TILES} />
                    <CircleMarker
                      center={[property.latitude!, property.longitude!]}
                      radius={9}
                      pathOptions={{ color: '#ffffff', weight: 2.5, fillColor: '#CE011F', fillOpacity: 1 }}
                    />
                  </MapContainer>
                  {(property.submarket || property.city) && (
                    <span className="pointer-events-none absolute bottom-3 left-3 z-[500] rounded-sm bg-gray-900/80 px-2 py-1.5 text-[10px] font-semibold uppercase leading-none tracking-[.1em] text-white">
                      {property.submarket ? `${property.submarket} submarket` : property.city}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setMapExpanded(true)}
                    className="absolute bottom-2.5 right-2.5 z-[500] h-[30px] rounded-lg bg-gray-900/80 px-[11px] text-[11.5px] font-semibold leading-none text-white hover:bg-gray-900"
                  >
                    Expand
                  </button>
                </div>
                {(property.cross_street || property.opportunity_zone) && (
                  <div className="grid grid-cols-2 gap-px border-t border-gray-150 bg-gray-150">
                    <div className="bg-white px-4 py-3">
                      <div className="mb-1 text-[10.5px] leading-tight text-gray-500">Cross street</div>
                      <div className="text-[13px] font-semibold leading-tight text-gray-800">{property.cross_street || '—'}</div>
                    </div>
                    <div className="bg-white px-4 py-3">
                      <div className="mb-1 text-[10.5px] leading-tight text-gray-500">Opportunity zone</div>
                      <div className="text-[13px] font-semibold leading-tight text-gray-800">{property.opportunity_zone ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* The dark deal-status block IS the pipeline stepper: click a stage to set it (it drives
                the map pin colors too); click the current stage to clear it. */}
            <div className="rounded-xl bg-gray-900 px-5 py-[18px] text-white">
              <div className="mb-2.5 text-[9.5px] font-bold uppercase leading-none tracking-[.14em] text-primary-500">Deal status</div>
              <div className="mb-3.5 flex flex-wrap items-center gap-2 text-[14px] font-medium leading-snug">
                {stageIdx >= 0 ? `${PIPELINE_STEPS[stageIdx].label} · stage ${stageIdx + 1} of ${PIPELINE_STEPS.length}` : 'Not in the pipeline'}
                {(pipeline === 'dead' || pipeline === 'watch') && (
                  <span className="rounded-full bg-white/15 px-2 py-1 text-[10px] font-semibold uppercase leading-none tracking-wide text-white/80">{pipeline}</span>
                )}
              </div>
              <div className="mb-2.5 flex gap-1">
                {PIPELINE_STEPS.map((s, i) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => handleSetPipeline(stageIdx === i ? 'untracked' : s.key)}
                    disabled={pipelineSaving}
                    title={stageIdx === i ? 'Click to clear stage' : `Set stage: ${s.label}`}
                    aria-label={stageIdx === i ? 'Clear stage' : `Set stage: ${s.label}`}
                    className={`h-[3px] flex-1 transition-colors disabled:opacity-60 ${i <= stageIdx ? 'bg-primary-500' : 'bg-white/20'} hover:bg-primary-400`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-[10px] font-medium uppercase leading-none tracking-[.06em] text-white/55">
                {PIPELINE_STEPS.map((s, i) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => handleSetPipeline(stageIdx === i ? 'untracked' : s.key)}
                    disabled={pipelineSaving}
                    className={`hover:text-white disabled:opacity-60 ${i === stageIdx ? 'font-bold text-white' : ''}`}
                  >
                    {s.short}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Fullscreen map overlay */}
        {mapExpanded && property.latitude != null && property.longitude != null && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-gray-900/55 p-4 sm:p-8" onClick={() => setMapExpanded(false)}>
            <div className="relative h-[78vh] w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-lg" onClick={(e) => e.stopPropagation()}>
              <MapContainer
                center={[property.latitude!, property.longitude!]}
                zoom={16}
                className="h-full w-full"
                scrollWheelZoom
                attributionControl={false}
              >
                <TileLayer {...STREET_TILES} />
                <CircleMarker
                  center={[property.latitude!, property.longitude!]}
                  radius={10}
                  pathOptions={{ color: '#ffffff', weight: 3, fillColor: '#CE011F', fillOpacity: 1 }}
                />
              </MapContainer>
              <span className="absolute bottom-3 left-3 z-[600] rounded-sm bg-gray-900/70 px-2.5 py-1.5 text-xs font-medium text-white">
                {property.address}{property.city ? `, ${property.city}` : ''}
              </span>
              <button
                type="button"
                onClick={() => setMapExpanded(false)}
                className="absolute right-3 top-3 z-[600] h-[34px] rounded-lg bg-white px-3 text-[12.5px] font-semibold text-gray-800 shadow-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="text-right text-[11px] text-gray-400">
          Created {formatDate(property.created_at)} · Updated {formatDate(property.updated_at)}
          {property.source ? ` · Source: ${property.source}` : ''}
        </div>
      </div>

      {/* Enable Management Modal - OO5c: through the one Dialog primitive. It keeps its own
          z-[1200] layer, which is what put it above this page's other overlays. */}
      <Dialog
        open={showMgmtModal}
        onClose={() => setShowMgmtModal(false)}
        title="Enable Asset Management"
        size="md"
        zClassName="z-[1200]"
        onSubmit={async (e) => {
              e.preventDefault();
              setMgmtSaving(true);
              try {
                const res = await api.updatePropertyManagement(id!, {
                  is_managed: true,
                  management_tier: mgmtForm.management_tier,
                  management_fee_percent: mgmtForm.management_fee_percent ? Number(mgmtForm.management_fee_percent) : null,
                  management_start_date: mgmtForm.management_start_date || null,
                  management_notes: mgmtForm.management_notes || null,
                });
                setProperty(prev => prev ? { ...prev, ...res.property } : null);
                setShowMgmtModal(false);
              } catch (err) { console.error(err); } finally { setMgmtSaving(false); }
            }}
        footer={<>
          <Button type="button" variant="outline" onClick={() => setShowMgmtModal(false)}>Cancel</Button>
          <Button type="submit" disabled={mgmtSaving} isLoading={mgmtSaving}>Enable Management</Button>
        </>}
      >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Management Tier *</label>
                <select value={mgmtForm.management_tier} onChange={e => setMgmtForm(f => ({ ...f, management_tier: e.target.value as ManagementTier }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="asset_management">Asset Management (Full Service)</option>
                  <option value="asset_oversight">Asset Oversight</option>
                  <option value="asset_monitoring">Asset Monitoring</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Management Fee %</label>
                  <input type="number" step="0.01" placeholder="e.g. 5.00" value={mgmtForm.management_fee_percent} onChange={e => setMgmtForm(f => ({ ...f, management_fee_percent: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={mgmtForm.management_start_date} onChange={e => setMgmtForm(f => ({ ...f, management_start_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={2} value={mgmtForm.management_notes} onChange={e => setMgmtForm(f => ({ ...f, management_notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Optional notes about the management agreement" />
              </div>
            </div>
      </Dialog>

      {/* The property database's own LOI entry. No deal here, so the letter is generated and
          downloaded rather than filed - a filing with no reader is a filing nobody sees. */}
      <LoiGeneratorDialog
        open={showLoiDialog}
        onClose={() => { setShowLoiDialog(false); setLoiFromChat({}); }}
        prefill={{
          address: (property as any)?.address ?? null,
          city: (property as any)?.city ?? null,
          state: (property as any)?.state ?? 'CA',
          zip: (property as any)?.zip ?? null,
          buildingSf: (property as any)?.building_size ?? null,
          county: (property as any)?.county ?? null,
          // AIW1-F6 (the 117th): the record's acreage and land SF fill the boxes as a PAIR, so the RE line states them.
          ...recordSiteSize({ acres: (property as any)?.lot_size_acres, lotSf: (property as any)?.land_area_sf }, loiFromChat),
          // Last, so a term he just stated beats anything the record offers.
          ...loiFromChat,
          // ⭐ Spec 3.9: this record, for the letter to say where it differs from it.
          recordFacts: recordFactsOf({
            address: (property as any)?.address, city: (property as any)?.city, zip: (property as any)?.zip,
            buildingSf: (property as any)?.building_size,
            // ⭐ The 114th: the record's acreage, from the column that is always acres - so a figure he states differs out loud.
            acres: (property as any)?.lot_size_acres,
            lotSf: (property as any)?.land_area_sf,
          }),
        }}
      />

      {/**
        * LP1.2 - the Proposal to Lease. ⛔ NOT the listing proposal: this is the tenant-side letter.
        * It downloads rather than files, because filing is LP1.5.
        * ⛔ THE PROPERTY TYPE IS NOT PREFILLED, deliberately. It is the single most consequential
        * choice on the form - it selects which sections the letter carries - and this record's
        * `property_type` does not map onto the five LEASE families without guessing. A wrong guess
        * here silently drops or adds clauses on a letter under his DRE number.
        */}
      <LeaseProposalGeneratorDialog
        open={showLeaseProposal}
        onClose={() => { setShowLeaseProposal(false); setLeaseFromChat({}); }}
        /**
         * LP1.5 - the record this page is showing, so the letter can be FILED on it. ⛔ Passing it
         * does not file anything: the dialog's box is off until he ticks it, and the route files
         * only when the id actually arrives.
         */
        propertyId={id ?? null}
        propertyLabel={[(property as any)?.address, (property as any)?.city].filter(Boolean).join(', ') || null}
        prefill={{
          address: (property as any)?.address ?? null,
          city: (property as any)?.city ?? null,
          state: (property as any)?.state ?? 'CA',
          zip: (property as any)?.zip ?? null,
          squareFeet: (property as any)?.building_size ?? null,
          apn: (property as any)?.apn ?? null,
          /**
           * LP1.4, LAST - so a term he just stated in chat beats what the record offers, the same
           * arrangement `loiFromChat` has. ⛔ It carries only what he SAID; the model is told never
           * to infer a rent, a term, a commencement or a square footage from a listing.
           * ⛔⛔ AND THE PROPERTY TYPE STILL IS NOT PREFILLED FROM THE RECORD - only from his own
           * words, if he used them. `property_type` does not map onto the five LEASE families, and
           * a wrong one silently drops or adds clauses.
           */
          ...leaseFromChat,
        }}
      />

      {/* The property database's listing-proposal entry - the screen he actually works from
          when deciding what to ask for a property. No deal, so it downloads. */}
      <ProposalGeneratorDialog
        open={showProposalDialog}
        onClose={() => setShowProposalDialog(false)}
        /* ⭐ DOC-READ (the 157th): the form reads THIS record itself - its facts and, where it is blank, the title
           report or property profile filed on it (the owners in letter order, their mailing address, the type and
           size, the APN, the year). ⭐ The 98th's rule stands: anything he stated in chat goes on top, and the page
           still owns WHICH property this is - the identity fields are stripped before they get here. The record's own
           values are the fallback for a failed read only. */
        recordId={(property as any)?.id ?? null}
        prefill={proposalFromChat}
        fallback={{
          addressLine: (property as any)?.address ?? null,
          city: (property as any)?.city ?? null,
          zoning: (property as any)?.zoning ?? null,
          ownerName: (property as any)?.owner_name ?? null,
        }}
      />

      {/* The property database's brochure / OM entry. No deal, so it prints and downloads. */}
      <BrochureGeneratorDialog
        open={showBrochureDialog}
        onClose={() => setShowBrochureDialog(false)}
        masterPropertyId={property?.id}
        prefill={brochurePrefill ?? {
          // Fallback only - identity from the row already on screen, so a failed prefill fetch
          // still opens a usable editor rather than an empty one.
          addressLine: (property as any)?.address ?? null,
          city: (property as any)?.city ?? null,
          state: (property as any)?.state ?? null,
          zip: (property as any)?.zip ?? null,
          county: (property as any)?.county ?? null,
          zoning: (property as any)?.zoning ?? null,
          apn: (property as any)?.apn ?? null,
        }}
      />

      {/* A5 duplicate prompt. No documentId on this path, so availableActions omits Attach. */}
      {duplicates && (
        <DuplicatePropertyDialog
          candidates={duplicates}
          busy={addingToDeal || startingDeal}
          onChoose={handleDuplicateChoice}
          onCancel={() => setDuplicates(null)}
        />
      )}
    </Layout>
  );
}
